"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Box, Button, Container, Paper, Stack, TextField, Typography } from "@mui/material";

type Step = "email" | "code";

export default function PartnerLoginPage() {
  const router = useRouter();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/partner/request-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const body = await res.json();
        setError(body.error ?? "Something went wrong");
        return;
      }

      setStep("code");
    } catch {
      setError("Unexpected error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/partner/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });

      if (!res.ok) {
        const body = await res.json();
        setError(body.error ?? "Invalid or expired code");
        return;
      }

      router.push("/partner");
    } catch {
      setError("Unexpected error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 8, md: 12 } }}>
      <Paper
        elevation={0}
        sx={{ p: { xs: 3, md: 5 }, border: 1, borderColor: "divider", borderRadius: 4 }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              Partner Portal
            </Typography>
            <Typography color="text.secondary">
              {step === "email"
                ? "Enter your email to receive a login code."
                : `We sent a 6-digit code to ${email}`}
            </Typography>
          </Box>

          {step === "email" ? (
            <Box component="form" onSubmit={handleRequestOtp}>
              <Stack spacing={2.5}>
                <TextField
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoFocus
                />
                {error && (
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                )}
                <Button type="submit" variant="contained" size="large" disabled={loading} fullWidth>
                  {loading ? "Sending..." : "Send login code"}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerifyOtp}>
              <Stack spacing={2.5}>
                <TextField
                  label="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  fullWidth
                  autoFocus
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                />
                {error && (
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                )}
                <Button type="submit" variant="contained" size="large" disabled={loading} fullWidth>
                  {loading ? "Verifying..." : "Sign in"}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setError(null);
                  }}
                >
                  Use a different email
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
